<template>
<div>
  <div v-if="loading || categoriesLoading || !currentCommunity"
    class="container-sm background-white text-center ">
    <b-spinner label="Loading" class="large-spinner"></b-spinner>
  </div>
  <div v-else class="container-sm background-white">
      <template>
        <b-modal id='cannot-edit-assessment'
          hide-header-close
          no-close-on-esc
          no-close-on-backdrop
          ok-only
          @ok='cancel'>
          <template #modal-header="{ }">
            <h3>Community cannot be edited.</h3>
          </template>
          <div class="d-block text-center">
            <p>Community must be downloaded and not finished or uploaded.</p>
          </div>
        </b-modal>
        <template>
          <!-- Validation errors, if any, will show at the very top of the page
            right below the top navbar -->
          <div v-if="validationErrorMessage" class='validation-errors'>
            <div class="text-align-centered">
              <strong>Assessment is missing required details.</strong> Please review section(s):
            </div>
            <div class="text-align-centered"><strong>{{ validationErrorMessage }}</strong></div>
          </div>
          <!-- Sticky Return to Home subnav bar -->
          <b-navbar sticky class="return-home">
            <b-navbar-nav>
              <span><font-awesome-icon icon="caret-left" size="lg" /></span>
              <router-link :to="{ name: 'home' }">Return to Community List</router-link>
            </b-navbar-nav>
          </b-navbar>
          <!-- Top section above the cards -->
          <div class='assessment-header'>
            <div class='assessment-title'>
              {{ currentCommunity.ProjectName }} -
              {{ currentCommunity.CommunityName }}
            </div>
            <div class='assessment-details'>
              <span class='bold-label'>
                City/County:
              </span>
              <template v-if='currentCommunity.City'>
                {{ currentCommunity.City }} /
              </template>
              {{ currentCommunity.County }}
            </div>
            <div class='assessment-details'>
              <span class='bold-label'>
                Latitude:
              </span>
              {{ latitude}}
              </div>
              <div class='assessment-details'>
              <span class='bold-label'>
              Longitude:
              </span>
              {{ longitude}}
            </div>
            <div class='assessment-details'>
              <span class='bold-label'>
                Community Area:
              </span>
              {{ calculateCommunityArea(currentCommunity.GisAcres) }}
            </div>
            <div class='instructional-text'>
                Enter assessment details in the sections below. Photos and Assessment Comments are
                optional; all other fields are required to complete and upload the assessment.
            </div>
          </div>
          <!-- Assessment Categories loop -->
          <b-card
            no-body
            class="mb-1"
            v-for="assessmentCategory in assessmentCategories"
            :key="assessmentCategory.HazardCategoryId"
          >
            <b-card-header header-tag="header" class="p-1" role="tab">
              <b-button
                squared
                :pressed="false"
                class='category-button'
                block
                v-b-toggle="`accordian-${assessmentCategory.HazardCategoryId}`"
                variant="card-header">
                <font-awesome-icon class="when-opened float-left" icon='caret-down' size="lg" />
                <font-awesome-icon class="when-closed fa-w-10 float-left"
                  icon='caret-right' size="lg" />
                <span class="float-left card-header-text">
                  {{ assessmentCategory.DisplayName }}
                </span>
                <div class='float-right'>
              ({{ getCompletedRequiredFieldCountByCategory(assessmentCategory.HazardCategoryId) }}
              of
              {{ getRequiredFieldCountByCategory(assessmentCategory.HazardCategoryId) }})
                </div>
              </b-button>
            </b-card-header>
            <b-collapse :id="`accordian-${assessmentCategory.HazardCategoryId}`"
              v-model="sectionShowCollapse[assessmentCategory.HazardCategoryId]">
              <b-card-body>
                <b-row class="form-row"
                  v-for="cls in assessmentCategory.HazardClasses"
                  :key="cls.HazardClassId">
                  <b-col sm="3">
                    <label class='bold-label-in-card'
                      :for="`type-${cls.Name}`">{{ cls.Description }}:
                    </label>
                  </b-col>
                  <b-col sm="9">
                    <!-- note: initially, the only text field is NumberOfHomes which can only be a
                    number. If other pure text fields get introduced, we will need to adjust our
                    approach to allow non-numeric data in this field -->
                    <template v-if="cls.Type === 'text'">
                      <b-form-input
                        :id="`${cls.Name}`"
                        type="text"
                        :state="getBFormInputState(assessmentCategory.HazardCategoryId, cls.Name)"
                        :required="cls.IsRequired"
                        v-model="localCommunity[cls.Name]"
                        :aria-describedby="`input-live-help input-live-feedback-${cls.Name}`"
                        @input="updateModel">
                      </b-form-input>
                      <!-- This will only be shown if the preceding input has an invalid state -->
                      <b-form-invalid-feedback :id="`input-live-feedback-${cls.Name}`">
                        {{ cls.Description }} is required and must be numeric
                      </b-form-invalid-feedback>
                    </template>
                    <template v-if="cls.Type === 'datetime'">
                      <date-picker
                        v-model="localCommunity[cls.Name]"
                        :aria-describedby="`input-live-help input-live-feedback-${cls.Name}`"
                        :id="`${cls.Name}`"
                        :state="getRequiredFieldState(assessmentCategory.HazardCategoryId,
                          cls.Name)"
                        type="date"
                        :disabled-date="notInTheFuture"
                        :clearable = "false"
                        :format="'MM/DD/YYYY'"
                        :input-class="getRequiredFieldState(assessmentCategory.HazardCategoryId,
                          cls.Name) ? 'mx-input form-control is-valid':'mx-input'"
                        @input="updateModel">
                      </date-picker>
                      <!-- This will only be shown if the preceding input has an invalid state -->
                      <b-form-invalid-feedback :id="`input-live-feedback-${cls.Name}`">
                        {{ cls.Description }} is required
                      </b-form-invalid-feedback>
                    </template>
                    <template v-if="cls.Type === 'lookup'">
                      <b-form-group>
                        <b-form-radio-group
                          class='full-width-radio'
                          name="radio-options"
                          v-model="localCommunity[cls.Name]"
                          :aria-describedby="`input-live-help input-live-feedback-${cls.Name}`"
                          :state=
                          "getRequiredFieldState(assessmentCategory.HazardCategoryId, cls.Name)"
                          :options="conditions(cls)"
                          buttons
                          button-variant="secondary"
                          stacked
                          :id="`${cls.Name}`"
                          @input="updateModel">
                        </b-form-radio-group>
                      </b-form-group>
                      <!-- This will only be shown if the preceding input has an invalid state -->
                      <b-form-invalid-feedback :id="`input-live-feedback-${cls.Name}`">
                        {{ cls.Description }} is required
                      </b-form-invalid-feedback>
                    </template>
                    <template v-if="cls.Type === 'fireDepartment'">
                      <multiselect
                        v-model="fireDepartmentSelected"
                        :options="fireDepartments"
                        placeholder="Select one or type to filter"
                        label="fdName"
                        track-by="id"
                        select-label=""
                        deselect-label="Click or tap to remove selection."
                        :aria-describedby="`input-live-help input-live-feedback-${cls.Name}`"
                        :id="`${cls.Name}`"
                        @select="updateFireDepartment(cls.Name, fireDepartmentSelected)"
                        @remove="removeStyleAndUpdateModel"
                      >
                        <span slot="noResult">
                          No results. Please change your search term.
                        </span>
                      </multiselect>
                      <!-- Uncomment the below to debug issues with multiselect component above -->
                      <!-- <pre>
                        <code>The fireDepartmentSelected is: {{fireDepartmentSelected}}</code>
                      </pre>
                      <pre>
                        <code>
                        The local community {{cls.Name}} is: {{localCommunity[cls.Name]}}
                        </code>
                      </pre> -->
                      <!-- This will only be shown if the preceding input has an invalid state -->
                      <b-form-invalid-feedback :id="`input-live-feedback-${cls.Name}`">
                        {{ cls.Description }} is required
                      </b-form-invalid-feedback>
                    </template>
                  </b-col>
                </b-row>
              </b-card-body>
            </b-collapse>
          </b-card>
          <!-- Photos -->
          <b-card
            no-body
            class="mb-1"
          >
            <b-card-header header-tag="header" class="p-1" role="tab">
              <b-button
                squared
                :pressed="false"
                class='category-button'
                block
                v-b-toggle.collapsePhotos
                variant="card-header">
                <font-awesome-icon class="when-opened float-left" icon='caret-down' size="lg" />
                <font-awesome-icon class="when-closed fa-w-10 float-left"
                  icon='caret-right' size="lg" />
                <span class="float-left card-header-text">
                  Photos
                </span>
              </b-button>
            </b-card-header>
            <b-collapse id="collapsePhotos"
              v-model="showPhotos">
              <b-card-body>
                <div class="instructional-text">
                  <p class="margin-bottom-25px">
                    Upload up to 3 photos (landscape orientation recommended).
                  </p>
                </div>
                <b-row >
                  <b-col sm="4">
                    <base-image-input v-model="localCommunity.Image1"
                      @input="updateImage"
                      :placeholderText="'Choose Image 1'"
                      :imageIndexName="'Image1'"
                      :enabled="true"/>
                  </b-col>
                  <b-col sm="4">
                    <base-image-input v-model="localCommunity.Image2"
                      @input="updateImage"
                      :placeholderText="'Choose Image 2'"
                      :imageIndexName="'Image2'"
                      :enabled="isNotEmpty(localCommunity.Image1)"/>
                  </b-col>
                  <b-col sm="4">
                    <base-image-input v-model="localCommunity.Image3"
                      @input="updateImage"
                      :placeholderText="'Choose Image 3'"
                      :imageIndexName="'Image3'"
                      :enabled="isNotEmpty(localCommunity.Image1)
                        && isNotEmpty(localCommunity.Image2)"/>
                  </b-col>
                </b-row>
              </b-card-body>
            </b-collapse>
          </b-card>
          <!-- Comments -->
          <b-card
            no-body
            class="mb-1"
          >
            <b-card-header header-tag="header" class="p-1" role="tab">
              <b-button
                squared
                :pressed="false"
                class='category-button'
                block
                v-b-toggle.collapseComments
                variant="card-header">
                <font-awesome-icon class="when-opened float-left" icon='caret-down' size="lg" />
                <font-awesome-icon class="when-closed fa-w-10 float-left"
                  icon='caret-right' size="lg" />
                <span class="float-left card-header-text">
                  Assessment Comments
                </span>
              </b-button>
            </b-card-header>
            <b-collapse id="collapseComments"
              v-model="showComments">
              <b-card-body>
                <b-row class="form-row">
                  <b-form-textarea
                    :id="localCommunity['Comments']"
                    type="text"
                    :required="false"
                    v-model="localCommunity['Comments']"
                    placeholder="Comments"
                    rows="5"
                    maxlength="500"
                    @input="updateModel">
                  </b-form-textarea>
                </b-row>
              </b-card-body>
            </b-collapse>
          </b-card>
          <!-- Review Ratings -->
          <b-card
            no-body
            class="mb-1"
          >
            <b-card-header header-tag="header" class="p-1" role="tab">
              <b-button
                squared
                :pressed="false"
                class='category-button'
                block
                v-b-toggle.collapseReview
                variant="card-header">
                <font-awesome-icon class="when-opened float-left" icon='caret-down' size="lg" />
                <font-awesome-icon class="when-closed fa-w-10 float-left"
                  icon='caret-right' size="lg" />
                <span class="float-left card-header-text">
                  Review Ratings
                </span>
              </b-button>
            </b-card-header>
            <b-collapse id="collapseReview"
              v-model="showRatings">
              <b-card-body>
                <Ratings
                  :localCommunity="localCommunity"
                  :requiredFieldCount="requiredFieldsCountObj"
                  :incompleteCategories="incompleteCategoriesObj"
                />
              </b-card-body>
            </b-collapse>
          </b-card>
          <!-- Finish Assessment -->
          <b-card
            no-body
            class="mb-1 p-1"
          >
            <b-button @click="markComplete" variant="primary"
             aria-label="Finish Assessment"
             class="finish-assessment-btn">
              Finish Assessment
            </b-button>
          </b-card>
        </template>
      </template>
  </div>
</div>

</template>

<script>
import { mapGetters, mapState } from 'vuex';
import helpers from '@/mixins/helpers';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import Ratings from '@/components/Ratings.vue';
import Multiselect from 'vue-multiselect';
import BaseImageInput from '../components/BaseImageInput.vue';

export default {
  name: 'Assessment',
  components: {
    Ratings,
    BaseImageInput,
    DatePicker,
    Multiselect,
  },
  mixins: [helpers],
  data() {
    return {
      localCommunity: {},
      numericFieldsState: {},
      requiredFieldsState: {},
      validationErrorMessage: null,
      sectionShowCollapse: {},
      showRatings: false,
      showPhotos: false,
      showComments: false,
      fireDepartmentSelected: null,
      multiselectIsValid: null,
    };
  },
  computed: {
    ...mapState({
      communityAssessorUrl: (state) => state.config.communityAssessorUrl,
      loggedIn: (state) => state.loggedIn,
      assessmentCategories: (state) => state.assessmentCategories,
      loading: (state) => state.loading,
      categoriesLoading: (state) => state.categoriesLoading,
      fireDepartments: (state) => state.fireDepartments,
      currentCommunity: (state) => state.currentCommunity,
    }),
    ...mapGetters({
      // map `this.doneCount` to `this.$store.getters.doneTodosCount`
      numericFields: 'numericFields',
      requiredFieldsCountObj: 'requiredFieldsCountObj',
      incompleteCategoriesObj: 'incompleteCategoriesObj',
    }),
    latitude() {
      if (!this.currentCommunity || !this.currentCommunity.LongLat) {
        return '';
      }
      const aBits = this.currentCommunity.LongLat
        .replace('POINT (', '')
        .replace(')', '')
        .replace(' ', ', ')
        .split(', ');
      return this.getDMS(Number(aBits[1]));
    },
    longitude() {
      if (!this.currentCommunity || !this.currentCommunity.LongLat) {
        return '';
      }
      const aBits = this.currentCommunity.LongLat
        .replace('POINT (', '')
        .replace(')', '')
        .replace(' ', ', ')
        .split(', ');
      return this.getDMS(Number(aBits[0]), true);
    },
  },
  mounted() {
    const communityId = parseInt(this.$route.params.community_id, 10);

    // TODO: See if we can better manage all these calls
    this.$store.dispatch('fetchConfiguration').then(() => {
      this.$store.dispatch('refreshLoggedInStatus').then(() => {
        if (this.isLoggedOutAndOnline()) {
          this.$router.push({ name: 'LogIn' });
        }
        this.$store.dispatch('fetchAssessmentCategories').then(() => {
          this.setRequiredFieldsStateValues();
          this.setNumericFieldsState();

          /* eslint-disable no-param-reassign */
          this.sectionShowCollapse = this.assessmentCategories
            .reduce((obj, item) => {
              obj[item.HazardCategoryId] = false;
              return obj;
            }, {});
          /* eslint-enable no-param-reassign */
          this.showPhotos = false;
          this.showRatings = false;
          this.showComments = false;
        });
        this.$store.dispatch('fetchFireDepartments').then(() => {
          this.$store.dispatch('setCurrentCommunityById', communityId).then(() => {
            if (this.currentCommunity) {
              this.localCommunity = this.currentCommunity;

              // Make sure we set up the multiselect component with the corresponding
              // fire dept id value and text label, if the fire department was previously selected
              const fireDepartmentIdSelected = this.currentCommunity.FireDepartmentId;
              if (fireDepartmentIdSelected) {
                // set up the multiselect properly
                this.fireDepartmentSelected = {
                  // eslint-disable-next-line max-len
                  fdName: this.fireDepartments.find((fd) => fd.id === fireDepartmentIdSelected)?.fdName,
                  id: fireDepartmentIdSelected,
                };
                // set up associated styles of multiselect element
                // we need to wait a second so we make sure that the DOM has been able to render
                // the multiselect properly, so we can modify the underlying style
                setTimeout(() => {
                  document.getElementsByClassName('multiselect__tags')[0].classList.add('multiselect-is-valid');
                }, 500);
                // set up associated styles of text input (number of homes) properly
                this.setNumericFieldsState();
                // set up completed/required count properly
                this.setRequiredFieldsStateValues();
              }

              if (this.localCommunity.status === 'downloaded') {
                this.localCommunity.status = 'started';
                this.localCommunity.AssessmentDate = new Date();
                this.updateModel();
              } else if (this.localCommunity.status !== 'started') {
                this.$bvModal.show('cannot-edit-assessment');
              }
            } else {
              this.$bvModal.show('cannot-edit-assessment');
            }
          }).catch((exc) => {
            console.log(`error: ${exc}`);
            console.log(JSON.stringify(exc));
          });
        });
      });
    });
  },
  methods: {
    removeStyleAndUpdateModel() {
      this.currentCommunity.FireDepartmentId = null;
      document.getElementsByClassName('multiselect__tags')[0].classList.remove('multiselect-is-valid');
      this.setRequiredFieldsStateValues();
    },
    getBFormInputState(hazCatId, clsName) {
      const ret = this.getRequiredFieldState(hazCatId, clsName)
        && this.numericFieldsState[clsName];
      return ret;
    },
    notInTheFuture(date) {
      return date > new Date(new Date().setHours(0, 0, 0, 0));
    },
    calculateCommunityArea(gisAcres) {
      let areaText = 'Not Available';
      if (gisAcres) {
        areaText = `${(Math.round(gisAcres * 10) / 10).toLocaleString()} acres`;
      }
      return areaText;
    },
    isNotEmpty(value) {
      if (value) {
        return true;
      }
      return false;
    },
    conditions(cls) {
      return cls.HazardConditions
        .map((x) => ({ value: x.Name, text: x.Name }));
    },
    cancel() {
      this.$router.push({ name: 'home' });
      window.scrollTo(0, 0);
    },
    deleteBase64StrImages() {
      /* eslint-disable no-plusplus */
      for (let imageIndexNumber = 1; imageIndexNumber <= 3; imageIndexNumber++) {
        const Base64StrName = `Image${imageIndexNumber}`;
        const FileName = `${Base64StrName}File`;
        if (this.localCommunity[FileName] && this.localCommunity[FileName].size > 0) {
          // delete the base64str images because we only need those for preview purposes
          // keep the image files since we will need to upload those to the server
          delete this.localCommunity[Base64StrName];
        }
      }
    },
    async finalizeAssessment() {
      if ((!this.localCommunity.Image1File
            && (this.localCommunity.Image2File || this.localCommunity.Image3File))
        || ((this.localCommunity.Image1File
            && this.localCommunity.Image3File) && !this.localCommunity.Image2File)
        || ((!this.localCommunity.Image1File
            && !this.localCommunityImage2File) && this.localCommunity.Image3File)) {
        await this.showOkModal('Selection Order Not Permitted', 'Please upload photos in slot order.');
      } else {
        this.deleteBase64StrImages();
        this.localCommunity.status = 'finished';
        this.updateModel();
        this.$store.dispatch('setFinishSuccess');
        this.$router.push({ name: 'home' });
        window.scrollTo(0, 0);
      }
    },
    showFinishAssessmentConfirmationModal() {
      this.$bvModal.msgBoxConfirm('Once finished, it cannot be edited again within this application.', {
        title: 'Are you sure you want to finish the assessment?',
        // size: 'sm',
        // buttonSize: 'sm',
        okVariant: 'success',
        okTitle: 'Finish',
        cancelTitle: 'Cancel',
        cancelVariant: 'danger',
        // footerClass: 'p-2',
        hideHeaderClose: true,
        centered: true,
      })
        .then((userClickedFinish) => {
          if (userClickedFinish) {
            this.finalizeAssessment();
          }
        });
    },
    markComplete() {
      // validate all of the categories
      // get a list of all incomplete categories
      if (this.incompleteCategoriesObj.length > 0) {
        this.validationErrorMessage = ` ${this.incompleteCategoriesObj.join(', ')}`;

        // collapse all sections
        this.assessmentCategories.forEach((cat) => {
          this.sectionShowCollapse[cat.HazardCategoryId] = false;
        });
        this.showComments = false;
        this.showPhotos = false;
        window.scrollTo(0, 0);
      } else {
        this.showFinishAssessmentConfirmationModal();
      }
    },
    updateImage({ imageIndexName, selectedImage64Str, imageFile }) {
      this.localCommunity[imageIndexName] = selectedImage64Str;
      this.localCommunity[`${imageIndexName}File`] = imageFile;
      this.updateModel();
    },
    updateFireDepartment(clsName, fdSelected) {
      this.localCommunity[clsName] = fdSelected.id;
      this.fireDepartmentSelected = fdSelected;
      document.getElementsByClassName('multiselect__tags')[0].classList.add('multiselect-is-valid');
      this.setRequiredFieldsStateValues();
      this.$store.dispatch('updateCurrentCommunity', this.localCommunity);
    },
    updateModel() {
      this.setNumericFieldsState();
      // only set requiredFieldsState to true if it has a value.
      // Not doing full validation until submit
      this.setRequiredFieldsStateValues();
      this.setScores();

      this.$store.dispatch('updateCurrentCommunity', this.localCommunity);
    },
    setScores() {
      const vm = this;
      this.assessmentCategories.forEach((cat) => {
        if (!cat.TotalsFlag) {
          let scoreValue = 0;
          cat.HazardClasses.forEach((hazClass) => {
            const selection = hazClass.HazardConditions
              .find((cond) => cond.Name === vm.localCommunity[hazClass.Name]);
            if (selection) {
              scoreValue += selection.Score;
            }
          });
          vm.localCommunity[cat.ScoreField] = scoreValue;
        }
      });
      const totalCat = this.assessmentCategories.find((cat) => cat.TotalsFlag);
      /* eslint-disable no-param-reassign */
      if (totalCat) {
        this.localCommunity[totalCat.ScoreField] = this.assessmentCategories
          .filter((cat) => !cat.TotalsFlag)
          .reduce((total, item) => {
            total += vm.localCommunity[item.ScoreField];
            return total;
          }, 0);
      }
    },
    setNumericFieldsState() {
      const vm = this;

      /* eslint-disable no-param-reassign */
      this.numericFieldsState = this.numericFields.reduce((obj, item) => {
        // manage the state so we know if to show an error
        if (Number.isNaN(Number(vm.localCommunity[item]))) {
          obj[item] = false;
        } else if (vm.localCommunity[item] == null
           || vm.localCommunity[item].trim() === '') {
          obj[item] = null;
        } else {
          obj[item] = true;
        }
        return obj;
      }, this.numericFieldsState);
    },
    setRequiredFieldsStateValues() {
      const vm = this;
      this.requiredFieldsState = this.assessmentCategories
        .map((cat) => (
          {
            id: cat.HazardCategoryId,
            fields: cat.HazardClasses.filter((hc) => hc.IsRequired)
              .map((hc) => {
                let state = null;
                if (vm.localCommunity[hc.Name]) {
                  state = true;
                }
                return {
                  name: hc.Name,
                  state,
                };
              }),
          }))
        .reduce((obj, item) => {
          /* eslint-disable no-param-reassign */
          obj[item.id] = item;
          /* eslint-enable no-param-reassign */
          return obj;
        }, {});
    },
    getRequiredFieldState(categoryId, fieldName) {
      if (!this.requiredFieldsState
          || !this.requiredFieldsState[categoryId]
          || !this.requiredFieldsState[categoryId].fields
            .find((field) => field.name === fieldName)) {
        return null;
      }
      return this.requiredFieldsState[categoryId].fields
        .find((field) => field.name === fieldName).state;
    },
    getCompletedRequiredFieldCountByCategory(categoryId) {
      if (this.requiredFieldsCountObj) {
        return this.requiredFieldsCountObj
          .filter((cat) => cat.id === categoryId)[0].completedRequiredCount;
      }
      return null;
    },
    getRequiredFieldCountByCategory(categoryId) {
      if (this.requiredFieldsCountObj) {
        return this.requiredFieldsCountObj.filter((cat) => cat.id === categoryId)[0].requiredCount;
      }
      return null;
    },
  },
};
</script>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
